import { visitIdClient } from "@/platform/api/visit-id.client"
import { buildBatch, chunkForBeacon, ImpressionsQueue } from "@/platform/observability/impressions/queue"
import type { QueueEntry, EventType } from "@/platform/observability/impressions/queue"
import { sendBatch, sendBeaconBatch } from "@/platform/observability/impressions/transport"

const TICK_INTERVAL_MS = 1_000
const BEACON_MAX_BYTES = 60_000
const BEACON_MAX_EVENTS = 200

let sharedQueue: ImpressionsQueue | undefined
let lifecycleRegistered = false
let tickIntervalId: ReturnType<typeof setInterval> | undefined

const queue = (): ImpressionsQueue => {
  sharedQueue ??= new ImpressionsQueue({ send: sendBatch })
  return sharedQueue
}

const flushViaBeacon = (): void => {
  const entries = queue().takeAllForBeacon()
  if (entries.length === 0) return

  const leftover: QueueEntry[] = []
  for (const chunk of chunkForBeacon(entries, new Date(), BEACON_MAX_BYTES, BEACON_MAX_EVENTS)) {
    if (!sendBeaconBatch(buildBatch(chunk, new Date()))) leftover.push(...chunk)
  }
  queue().requeue(leftover)
}

const ensureLifecycle = (): void => {
  if (lifecycleRegistered || typeof window === "undefined") return
  lifecycleRegistered = true

  tickIntervalId = setInterval(() => queue().tick(), TICK_INTERVAL_MS)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushViaBeacon()
  })
  window.addEventListener("pagehide", flushViaBeacon)
}

const dispatch = (type: EventType, serveId: string, occurredAt: Date): void => {
  ensureLifecycle()

  const occurredAtIso = occurredAt.toISOString()

  const visitId = visitIdClient()
  if (!visitId) return

  queue().enqueue({ type, serve_id: serveId, occurred_at: occurredAtIso, visit_id: visitId })
}

export const impressions = {
  impression: (serveId: string, occurredAt: Date): void => dispatch("impression", serveId, occurredAt),
  click: (serveId: string, occurredAt: Date = new Date()): void => dispatch("click", serveId, occurredAt),
  flush: (): void => {
    void queue().flush()
  },
}

export const bootIfNeeded = (): void => {
  if (typeof window !== "undefined" && queue().size > 0) ensureLifecycle()
}

bootIfNeeded()

export const __resetImpressionsForTests = (): void => {
  sharedQueue = undefined
  lifecycleRegistered = false
  if (tickIntervalId !== undefined) clearInterval(tickIntervalId)
  tickIntervalId = undefined
}
